import { observable, computed, makeObservable, runInAction, action } from 'mobx'
import { move } from '@dnd-kit/helpers'
import Wkt from 'wicket/wicket'
import api from 'api.js'
import { selectionTypes, defaultPolygonColor, ADDITIONAL_INFO_DEFAULT_INPUT } from 'constants.js'
import { geoJSONBounds, smoothFactor, mergePopulationDatas, downloadKML } from 'utils'
import RegionAnalysis from './RegionAnalysis'
import { catchError } from './ErrorStore'
import { uiStore, dataStore, rightSidebarStore } from './UIStore'
import { debounce } from 'lodash'
import axios from 'axios'

class Boundary {
  @observable remote_db_id
  @observable inner_id
  @observable name
  @observable created
  @observable.ref geoJSON
  @observable sidebarCache = new Map()
  @observable analysis = new RegionAnalysis()
  @observable isZipCode
  @observable is_owner = true
  @observable population_datas = null
  @observable color_id
  @observable transparency
  @observable targetGroupId
  @observable status
  @observable tag_ids
  @observable additional_info
  @observable notes = ''
  @observable contact_detail = { given_name: '', surname: '', email: '', address: '', country: '' }

  constructor ({ targetGroupId, remote_db_id, inner_id, name, geometry, created, isZipCode, is_owner = true, color_id, color_transparency, population_datas = null, shape_status = null, tag_ids = [], additional_info = [], notes = '', contact_detail = {} }) {
    makeObservable(this)
    this.targetGroupId = targetGroupId
    this.remote_db_id = remote_db_id
    this.inner_id = inner_id
    this.name = name
    this.created = created
    this.isZipCode = isZipCode
    this.is_owner = is_owner
    this.color_id = color_id
    this.transparency = color_transparency !== undefined ? color_transparency : 50
    this.population_datas = population_datas
    this.status = shape_status
    this.tag_ids = tag_ids
    const wkt = new Wkt.Wkt()
    this.geoJSON = wkt.read(geometry).toJson()
    this.additional_info = (additional_info || []).map(item => ({
      ...item,
      info_item: item.info_item != null ? item.info_item : item.fid
    }))
    this.notes = notes
    this.contact_detail = { given_name: '', surname: '', email: '', phone: '', address: '', country: '', ...contact_detail }
    if (population_datas) {
      this.analysis.load(this.pickPopulationDataForCurrentYear(population_datas), this.geoJSON)
    }
  }

  bounds () { return geoJSONBounds(this.geoJSON) }

  get id () {
    return this.remote_db_id || this.inner_id
  }

  getTypeLabel () {
    return 'Boundary'
  }

  findAdditionalInfoIndex (id) {
    return this.additional_info.findIndex(item => Number(item.info_item) === Number(id))
  }

  @catchError async saveAdditionalInfo (id) {
    const data = this.additional_info[this.findAdditionalInfoIndex(id)]
    if (!data) return
    const saveInfoApi = this.isZipCode ? api.postZipCodeBoundaryAdditionalInfo : api.postAdminBoundaryAdditionalInfo
    await saveInfoApi(this.inner_id, data)
  }

  getAdditionalInfoItemValue = (id) => {
    const item = this.additional_info[this.findAdditionalInfoIndex(id)]
    return item ? item.input : null
  }

  // The API has no delete for a single row, so clearing its input and saving is how a value is removed.
  @action deleteAdditionalInfoItem = (id) => {
    const index = this.findAdditionalInfoIndex(id)
    if (index === -1) return

    const item = this.additional_info[index]
    this.additional_info[index] = { ...item, input: { ...ADDITIONAL_INFO_DEFAULT_INPUT[item.type] } }
    this.debouncedSaveAdditionalInfo(id)
    this.debouncedSaveAdditionalInfo.flush()
  }

  @action updateAdditionalInfoItem = (id, field, value) => {
    const index = this.findAdditionalInfoIndex(id)

    if (index === -1) {
      const definition = dataStore.userAdditionalInfoItems.find(item => item.id === id)
      if (!definition) return

      this.additional_info.push({
        id: definition.id,
        order: this.additional_info.length + 1,
        info_item: definition.id,
        label: definition.label,
        type: definition.type,
        input: {
          ...ADDITIONAL_INFO_DEFAULT_INPUT[definition.type],
          [field]: value
        }
      })
    } else {
      const item = this.additional_info[index]
      this.additional_info[index] = {
        ...item,
        input: {
          ...item.input,
          [field]: value
        }
      }
    }

    this.debouncedSaveAdditionalInfo(id)
  }

  @action updateAdditionalInfoItemType = (id, type, label) => {
    const index = this.findAdditionalInfoIndex(id)
    if (index === -1) return

    const item = this.additional_info[index]
    if (item.type === type) return

    this.additional_info[index] = {
      ...item,
      type,
      label,
      input: {
        ...ADDITIONAL_INFO_DEFAULT_INPUT[type],
        title: item.input ? item.input.title : null
      }
    }

    this.debouncedSaveAdditionalInfo(id)
  }

  // Local only: the additional-info API stores per-field rows and has nowhere to put boundary notes.
  @action updateNotes = (value) => {
    this.notes = value
  }

  pendingAdditionalInfoIds = new Set()

  savePendingAdditionalInfo = debounce(() => {
    const ids = [...this.pendingAdditionalInfoIds]
    this.pendingAdditionalInfoIds.clear()
    ids.forEach(id => this.saveAdditionalInfo(id))
  }, 800)

  debouncedSaveAdditionalInfo = Object.assign(
    (id) => {
      this.pendingAdditionalInfoIds.add(id)
      this.savePendingAdditionalInfo()
    },
    { flush: () => this.savePendingAdditionalInfo.flush() }
  )

  @action updateContactDetail = (field, value) => {
    this.contact_detail = { ...this.contact_detail, [field]: value }
    this.debouncedSaveContactDetail()
  }

  debouncedSaveContactDetail = debounce(() => {
    this.saveContactDetail()
  }, 800)

  @catchError async saveContactDetail () {
    const saveContactApi = this.isZipCode ? api.postZipCodeBoundaryContactDetail : api.postAdminBoundaryContactDetail
    await saveContactApi(this.inner_id, this.contact_detail)
  }

  @catchError async addAdditionalInfoItem (item) {
    runInAction(() => { this.additional_info.push(item) })
    await this.saveAdditionalInfo()
  }

  @action sortAdditionalInfoItem (event) {
    const sorted_items = move(this.additional_info, event)
    this.additional_info = sorted_items
  }

  @computed get baseColor () {
    const item = dataStore.markerColors.find(item => item.id === this.color_id)
    return item ? item.active_color : defaultPolygonColor
  }

  @computed get getColor () {
    // Boundaries use the same cached shape-status palette as drawn polygons.
    const status = dataStore.shapeStatuses.find(item => item.id === this.status)
    if (status) return status.color
    return this.baseColor
  }

  get selection () {
    this.error = null
    return {
      item: this,
      type: this.isZipCode ? selectionTypes.ZIP_CODE_BOUNDARY : selectionTypes.BOUNDARY
    }
  }

  async loadAnalysis (cancelToken = null) {
    const config = cancelToken ? { cancelToken } : {}

    runInAction(() => {
      if (rightSidebarStore) rightSidebarStore.pyramidLoading = true
    })

    try {
      const [lsRes, wpRes] = await Promise.allSettled([
        this.isZipCode
          ? api.postZipCodeBoundaryAnalysis(
            { id: this.remote_db_id },
            { ...config, headers: { ...(config.headers || {}), 'Ds-Name': 'landscan', 'Ds-Year': '2016' } }
          )
          : api.postBoundaryAnalysis(
            { id: this.remote_db_id },
            { ...config, headers: { ...(config.headers || {}), 'Ds-Name': 'landscan', 'Ds-Year': '2016' } }
          ),
        this.isZipCode
          ? api.postZipCodeBoundaryAnalysis(
            { id: this.remote_db_id },
            { ...config, headers: { ...(config.headers || {}), 'Ds-Name': 'worldpop', 'Ds-Year': '2024' } }
          )
          : api.postBoundaryAnalysis(
            { id: this.remote_db_id },
            { ...config, headers: { ...(config.headers || {}), 'Ds-Name': 'worldpop', 'Ds-Year': '2024' } }
          )
      ])

      const lsData = lsRes.status === 'fulfilled' && lsRes.value && lsRes.value.data && lsRes.value.data.population_datas
      const wpData = wpRes.status === 'fulfilled' && wpRes.value && wpRes.value.data && wpRes.value.data.population_datas
      this.population_datas = mergePopulationDatas(this.population_datas || lsData, wpData) || lsData || wpData
      if (rightSidebarStore && rightSidebarStore.singlePolygonData && String(rightSidebarStore.singlePolygonId) === String(this.remote_db_id || this.inner_id)) {
        rightSidebarStore.singlePolygonData.population_datas = this.population_datas
      }

      this.analysis.load(
        this.pickPopulationDataForCurrentYear(this.population_datas),
        this.geoJSON
      )
    } catch (e) {
      if (axios.isCancel(e)) {
        console.info('Api request cancelled explicitly')
        return
      }
      console.warn('Error fetching boundary data: ', e)
      this.analysis.loading = false
    } finally {
      runInAction(() => {
        if (rightSidebarStore) rightSidebarStore.pyramidLoading = false
        uiStore.setProcessing(false)
      })
    }
  }

  pickPopulationDataForCurrentYear (population_datas) {
    if (Array.isArray(population_datas)) {
      return population_datas[0]
    }
    if (!population_datas) return null
    const currentYearKey = String(dataStore.defaultYear.result)
    // const currentYearKey = String(rightSidebarStore.latestYear)
    const currentYearEntry = population_datas[currentYearKey]
    const ds = dataStore.defaultDsName || 'landscan'
    if (currentYearEntry && Array.isArray(currentYearEntry)) {
      const match = currentYearEntry.find(d => d.type === ds)
      if (match) return match
      if (currentYearEntry[0]) return currentYearEntry[0]
    }
    const firstNonEmpty = Object.values(population_datas).find(v => v && Array.isArray(v) && v.length > 0)
    if (firstNonEmpty) {
      const match = firstNonEmpty.find(d => d.type === ds)
      if (match) return match
      return firstNonEmpty[0]
    }
    return null
  }

  reloadAnalysisForCurrentYear () {
    const picked = this.pickPopulationDataForCurrentYear(this.population_datas)
    if (picked) {
      this.analysis = new RegionAnalysis()
      this.analysis.load(picked, this.geoJSON)
    } else {
      this.analysis.loading = true
      this.loadAnalysis()
    }
  }

  @catchError async copy (targetGroupId) {
    if (this.isZipCode) {
      await api.postCopyZipCodeBoundary(this.inner_id, { target_group_id: targetGroupId })
    } else {
      await api.postCopyBoundary(this.inner_id, { target_group_id: targetGroupId })
    }
  }

  @catchError async updateStatus (shapeStatus) {
    const update = this.isZipCode ? api.patchZipCodeBoundaryStatus : api.patchBoundaryStatus
    runInAction(() => {
      this.status = shapeStatus.id
    })
    await update(this.inner_id, { shape_status: shapeStatus.id })
  }

  @catchError
  async updateTags (tagId) {
    const update = this.isZipCode ? api.postZipCodeBoundaryTags : api.postBoundaryTags
    runInAction(() => {
      if (!this.tag_ids.includes(tagId)) this.tag_ids.push(tagId)
    })
    await update(this.inner_id, { tag_ids: [tagId] })
  }

  @catchError
  async deleteTag (tagId) {
    const remove = this.isZipCode ? api.deleteZipCodeBoundaryTag : api.deleteBoundaryTag
    runInAction(() => {
      this.tag_ids = this.tag_ids.filter(id => tagId !== id)
    })
    await remove(this.inner_id, tagId)
  }

  async exportExcel () {
    await this.analysis.exportExcel(this.name)
  }

  async exportCSV () {
    await this.analysis.exportCSV(this.name)
  }

  @catchError async exportKml (kmlFormat) {
    const response = await api.getBoundaryKML(this.inner_id, !kmlFormat, { responseType: 'blob' })
    downloadKML(this.name, response.data, kmlFormat)
  }

  @catchError async deleteBoundary () {
    if (this.isZipCode) {
      await api.deleteZipCodeBoundary(this.inner_id)
    } else {
      await api.deleteBoundary(this.inner_id)
    }
  }

  @computed get smoothFactor () {
    return smoothFactor(this.geoJSON)
  }
}

export default Boundary
